import React, { useContext, useEffect, useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import BdsSegment from '../../../../../shared/components/v3/BdsSegment/BdsSegment';
import BdsButton from '../../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import GenericMenu from '../../../../../shared/components/v3/GenericMenu/GenericMenu';
import { FILTER_IMAGES } from '../../../../../shared/constants/images';
import { CLICK_ACTION, FILTER_REQUESTS, REQUESTS_TYPES } from '../../../../../shared/constants/tagManager';
import { CompanyContext } from '../../../../../shared/context/company/companyContext';
import { IFlexibleRemunerationRequest } from '../../../../../shared/interfaces/company';
import TagManagerService from '../../../../../shared/services/gtm/tagManagerService';
import { TRANSLATIONS_REQUESTS } from '../../../../../shared/translations/requests';
import { objectHasKey } from '../../../../../shared/utils/utils';

import './RequestFilter.scss';

interface IRequestFilter {
  onFilterChange: (string?: string) => void;
  onCategoryChange: (string: string) => void;
  requests: IFlexibleRemunerationRequest[];
}

const RequestFilter = ({ onFilterChange, onCategoryChange, requests }: IRequestFilter) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [showGenericMenu, setShowGenericMenu] = useState<boolean>(false);

  const { company } = useContext(CompanyContext);
  const { t } = useTranslation();

  const hasRequestFilter = useMemo(() => {
    const categories = new Set(requests.map((request) => request.category));
    return categories.size > 1;
  }, [requests]);

  const tabs = [
    {
      label: TRANSLATIONS_REQUESTS.table.status.all,
      value: undefined
    },
    {
      label: TRANSLATIONS_REQUESTS.table.status.pending,
      value: 'pending'
    },
    {
      label: TRANSLATIONS_REQUESTS.table.status.active,
      value: 'active'
    },
    {
      label: TRANSLATIONS_REQUESTS.table.status.rejected,
      value: 'rejected'
    },
    {
      label: TRANSLATIONS_REQUESTS.table.status.cancelled,
      value: 'cancelled'
    }
  ];

  const filterValue = useMemo(
    () => [
      { name: t(TRANSLATIONS_REQUESTS.table.filter.all), value: 'all' },
      { name: t(TRANSLATIONS_REQUESTS.table.filter.training), value: 'training' },
      { name: t(TRANSLATIONS_REQUESTS.table.filter.health), value: 'health' }
    ],
    [t]
  );

  const onTabChange = (index: number) => {
    setTabIndex(index);
    onFilterChange(tabs[index].value);
  };

  const changeOption = (value: string) => {
    handleGenericMenu();
    onCategoryChange(value);
    const filterOption = objectHasKey(value, REQUESTS_TYPES) ? REQUESTS_TYPES[value] : undefined;
    TagManagerService.pushEvent(CLICK_ACTION, FILTER_REQUESTS, { ...filterOption });
  };

  const handleGenericMenu = () => {
    setShowGenericMenu(!showGenericMenu);
  };

  useEffect(() => {
    setTabIndex(0);
  }, [company]);

  return (
    <div className="requests-filters-container">
      <div className="filter-row">
        <BdsSegment items={tabs.map((filter) => filter.label)} action={onTabChange} activeIndex={tabIndex} />
        {hasRequestFilter && (
          <div>
            <BdsButton action={handleGenericMenu} variant="secondary" size="s" iconLeft={FILTER_IMAGES}>
              {t(TRANSLATIONS_REQUESTS.table.filter.title)}
            </BdsButton>
            <GenericMenu
              setShowGenericMenu={setShowGenericMenu}
              showGenericMenu={showGenericMenu}
              title={t(TRANSLATIONS_REQUESTS.table.filter.title)}
              company={company}
              type="custom"
              className="requests-table-generic-menu"
              customData={{
                showButtonFilter: true,
                defaultValueOnFilterButton: true,
                data: filterValue,
                onChanged: changeOption,
                value: 'all'
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
export default RequestFilter;
